import { adminAxiosInstance } from "./adminAxiosInstance";

const REGISTERED_USERS_CACHE_TTL = 2 * 60 * 1000;
const REGISTERED_USER_SNAPSHOT_KEY = "agrogrid_admin_registered_user_snapshots";
const registeredUsersCache = new Map();
const registeredUsersRequests = new Map();
const guestUsersCache = new Map();
const guestUsersRequests = new Map();

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
};

const readSnapshots = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(REGISTERED_USER_SNAPSHOT_KEY)) || {};
  } catch {
    window.sessionStorage.removeItem(REGISTERED_USER_SNAPSHOT_KEY);
    return {};
  }
};

const storeSnapshots = (users) => {
  if (typeof window === "undefined") return;
  try {
    const snapshots = readSnapshots();
    users.forEach((user) => {
      if (user.id) snapshots[user.id] = user;
    });
    window.sessionStorage.setItem(REGISTERED_USER_SNAPSHOT_KEY, JSON.stringify(snapshots));
  } catch {
    // The directory remains usable when session storage is unavailable.
  }
};

const normalizeTags = (user, requestedSegment) => {
  const supplied = Array.isArray(user.tags)
    ? user.tags
    : Array.isArray(user.segments)
      ? user.segments
      : user.segment
        ? [user.segment]
        : [];
  const tags = supplied.map((tag) => String(tag).toLowerCase().replaceAll("_buyers", ""));
  if (requestedSegment === "frequent_buyers") tags.push("frequent");
  if (requestedSegment === "inactive_buyers") tags.push("inactive");
  if (requestedSegment === "new_users") tags.push("new");
  if (requestedSegment === "new_guests") tags.push("new");
  return [...new Set(tags)];
};

const normalizeRegisteredUser = (user, requestedSegment, accountKind) => {
  const firstName = user.firstname ?? user.first_name ?? user.firstName ?? "";
  const lastName = user.lastname ?? user.last_name ?? user.lastName ?? "";
  const name = (user.name ?? user.full_name ?? user.fullName ?? user.contact_name ?? [firstName, lastName].filter(Boolean).join(" ")) || "Unnamed user";
  const rawOrders = user.total_orders ?? user.orders_count ?? user.order_count ?? user.orders;
  const orders = Array.isArray(rawOrders) ? rawOrders.length : Number(rawOrders || 0);
  const totalSpend = Number(user.total_amount_spent ?? user.total_spent ?? user.total_spend ?? user.amount_spent ?? user.spent ?? 0);
  const joinedAt = user.joined_at ?? user.joined ?? user.created_at ?? user.createdAt;
  const lastActiveAt = user.last_active_at ?? user.last_active ?? user.last_seen_at;
  const lastOrderAt = user.last_order_date ?? user.last_order_at ?? user.last_purchase_at ?? user.last_purchase;

  const rawAddress = user.address?.formatted_address ?? user.address?.address ?? user.address ?? user.delivery_address;
  const normalizedAddress = typeof rawAddress === "string"
    ? rawAddress
    : [rawAddress?.street, rawAddress?.city ?? user.city, rawAddress?.state ?? user.state].filter(Boolean).join(", ") || "Not available";
  const guestIds = [...new Set(
    (Array.isArray(user.guest_ids) ? user.guest_ids.flat(Infinity) : [user.guest_ids])
      .map((id) => String(id || ""))
      .filter(Boolean)
  )];
  const id = String(user.id ?? user._id ?? user.user_id ?? user.guest_user_id ?? user.guest_id ?? guestIds[0] ?? "");
  const selectionIds = accountKind === "guest" && guestIds.length ? guestIds : id ? [id] : [];

  return {
    ...user,
    accountKind,
    id,
    guestIds,
    selectionIds,
    name,
    email: user.email || "Not available",
    phone: user.phone ?? user.phone_number ?? user.phonenumber ?? "Not available",
    type: user.role ?? user.customer_type ?? user.user_type ?? user.account_type ?? (user.is_farmer ? "Farmer" : accountKind === "guest" ? "Guest user" : "Registered user"),
    orders,
    lastPurchase: formatDate(lastActiveAt),
    lastActiveAt,
    lastOrderDate: formatDate(lastOrderAt),
    lastOrderAt,
    hasPushToken: Boolean(user.has_fcm_token ?? user.has_push_token ?? user.fcm_token),
    tags: normalizeTags(user, requestedSegment),
    address: normalizedAddress,
    joined: formatDate(joinedAt),
    totalSpend,
    successfulOrders: Number(user.successful_orders ?? user.completed_orders ?? 0),
    cancelledOrders: Number(user.cancelled_orders ?? user.canceled_orders ?? 0),
    recentOrders: Array.isArray(user.recent_orders) ? user.recent_orders : [],
  };
};

const requestParams = ({ segment = "all", days = 30, page = 1, pageSize = 10, sortBy = "orders", order = "desc", search = "" } = {}) => ({
  segment,
  days,
  page,
  pageSize,
  sortBy,
  order,
  ...(search?.trim?.() ? { search: search.trim() } : {}),
});

const normalizeResponse = (response, params, accountKind = "registered") => {
  const responseData = response?.data;
  const rawUsers = Array.isArray(responseData)
    ? responseData
    : responseData?.users ?? responseData?.registered_users ?? responseData?.guest_users ?? responseData?.items ?? responseData?.results ?? responseData?.data ?? response?.users ?? [];
  const paginationSource = response?.pagination ?? responseData?.pagination ?? response?.meta ?? responseData?.meta ?? {};
  const users = Array.isArray(rawUsers)
    ? rawUsers.map((user) => normalizeRegisteredUser(user, params.segment, accountKind))
    : [];

  storeSnapshots(users);

  return {
    status: response?.status !== false,
    message: response?.message,
    segment: response?.segment ?? params.segment,
    period_days: response?.period_days ?? params.days,
    summary: {
      total_matching_users: Number(response?.summary?.total_matching_users ?? response?.summary?.total_coagulated_guests ?? paginationSource.total_items ?? users.length),
      has_push_token_count: Number(response?.summary?.has_push_token_count ?? 0),
      has_phone_count: Number(response?.summary?.has_phone_count ?? 0),
      has_email_count: Number(response?.summary?.has_email_count ?? 0),
    },
    data: users,
    pagination: {
      total_items: Number(paginationSource.total_items ?? paginationSource.totalItems ?? paginationSource.total ?? users.length),
      current_page: Number(paginationSource.current_page ?? paginationSource.currentPage ?? paginationSource.page ?? params.page),
      total_pages: Number(paginationSource.total_pages ?? paginationSource.totalPages ?? Math.max(1, Math.ceil(users.length / params.pageSize))),
      pageSize: Number(paginationSource.pageSize ?? paginationSource.page_size ?? params.pageSize),
    },
  };
};

export const getRegisteredUsers = (options = {}) => {
  const { forceRefresh = false, ...requestOptions } = options;
  const params = requestParams(requestOptions);
  const cacheKey = JSON.stringify(params);
  const cached = registeredUsersCache.get(cacheKey);
  if (!forceRefresh && cached && Date.now() - cached.updatedAt < REGISTERED_USERS_CACHE_TTL) return Promise.resolve(cached.data);
  if (registeredUsersRequests.has(cacheKey)) return registeredUsersRequests.get(cacheKey);

  const request = adminAxiosInstance
    .get("/admin/internal/marketing/registered-users", { params })
    .then(({ data }) => {
      const result = normalizeResponse(data, params, "registered");
      if (!result.status) throw new Error(result.message || "Registered users could not be loaded.");
      registeredUsersCache.set(cacheKey, { data: result, updatedAt: Date.now() });
      return result;
    })
    .finally(() => registeredUsersRequests.delete(cacheKey));

  registeredUsersRequests.set(cacheKey, request);
  return request;
};

export const getGuestUsers = (options = {}) => {
  const { forceRefresh = false, ...requestOptions } = options;
  const params = requestParams(requestOptions);
  const cacheKey = JSON.stringify(params);
  const cached = guestUsersCache.get(cacheKey);
  if (!forceRefresh && cached && Date.now() - cached.updatedAt < REGISTERED_USERS_CACHE_TTL) return Promise.resolve(cached.data);
  if (guestUsersRequests.has(cacheKey)) return guestUsersRequests.get(cacheKey);

  const request = adminAxiosInstance
    .get("/admin/internal/marketing/guest-users", { params })
    .then(({ data }) => {
      const result = normalizeResponse(data, params, "guest");
      if (!result.status) throw new Error(result.message || "Guest users could not be loaded.");
      guestUsersCache.set(cacheKey, { data: result, updatedAt: Date.now() });
      return result;
    })
    .finally(() => guestUsersRequests.delete(cacheKey));

  guestUsersRequests.set(cacheKey, request);
  return request;
};

export const getRegisteredUserSnapshot = (userId) => readSnapshots()[userId] || null;

export const storeAdminUserSnapshot = (user, accountKind = "registered") => {
  const normalized = normalizeRegisteredUser(user, "all", accountKind);
  if (!normalized.id) return null;
  const existing = readSnapshots()[normalized.id];
  const snapshot = existing ? { ...normalized, ...existing } : normalized;
  storeSnapshots([snapshot]);
  return snapshot;
};

export const clearRegisteredUsersCache = () => {
  registeredUsersCache.clear();
  registeredUsersRequests.clear();
  guestUsersCache.clear();
  guestUsersRequests.clear();
  if (typeof window !== "undefined") window.sessionStorage.removeItem(REGISTERED_USER_SNAPSHOT_KEY);
};
